import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { startMagicLink } from '../lib/api';

export function SignInPage() {
  const { isAuthenticated, isLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isLoading && isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await startMagicLink(email.trim());
      setSent(true);
    } catch {
      setError('Something went wrong. Check your email address and try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100dvh',
        padding: 'var(--space-6)',
        background: 'var(--color-bg)',
      }}
    >
      <div style={{ width: '100%', maxWidth: '400px' }}>
        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-8)' }}>
          <h1
            style={{
              fontSize: '2rem',
              fontWeight: 800,
              color: 'var(--color-accent)',
              letterSpacing: '-0.03em',
            }}
          >
            Anchor
          </h1>
          <p style={{ color: 'var(--color-text-muted)', marginTop: 'var(--space-1)' }}>
            Admin console
          </p>
        </div>

        <div className="card">
          {sent ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-4) 0' }}>
              <h2 style={{ marginBottom: 'var(--space-3)' }}>Check your email</h2>
              <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9375rem' }}>
                We sent a sign-in link to <strong>{email}</strong>. It expires in
                15 minutes.
              </p>
              <button
                className="btn btn--ghost btn--sm"
                style={{ marginTop: 'var(--space-6)' }}
                onClick={() => setSent(false)}
              >
                Use a different email
              </button>
            </div>
          ) : (
            <>
              <h2 style={{ marginBottom: 'var(--space-5)' }}>Sign in</h2>

              <form onSubmit={handleSubmit} noValidate>
                <div className="form-group" style={{ marginBottom: 'var(--space-4)' }}>
                  <label className="form-label" htmlFor="email">
                    Work email
                  </label>
                  <input
                    id="email"
                    type="email"
                    className="form-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@yourorg.com"
                    autoComplete="email"
                    required
                    autoFocus
                  />
                </div>

                {error && (
                  <p
                    role="alert"
                    style={{
                      color: 'var(--color-danger)',
                      fontSize: '0.875rem',
                      marginBottom: 'var(--space-4)',
                    }}
                  >
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  className="btn btn--primary"
                  style={{ width: '100%' }}
                  disabled={submitting || !email.trim()}
                >
                  {submitting ? 'Sending…' : 'Send sign-in link'}
                </button>
              </form>

              <p
                style={{
                  marginTop: 'var(--space-5)',
                  fontSize: '0.8125rem',
                  color: 'var(--color-text-subtle)',
                  textAlign: 'center',
                }}
              >
                For Apple Sign In, open the Anchor iOS app and sign in there first.
                <br />
                Enterprise SSO coming in v1.1.
              </p>
            </>
          )}
        </div>

        <p
          style={{
            marginTop: 'var(--space-6)',
            textAlign: 'center',
            fontSize: '0.8125rem',
            color: 'var(--color-text-subtle)',
          }}
        >
          Need an account?{' '}
          <a
            href="https://howisyourenginerunning.com"
            style={{ color: 'var(--color-accent)' }}
          >
            Learn about Anchor
          </a>
        </p>
      </div>
    </div>
  );
}
